import React, { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from './cn';
import { Icon } from './base/Icon';

/**
 * TrendChart — กราฟตามช่วงเวลา แบบแท่ง (ชุดเดียว) หรือเส้น (ไม่เกิน 2 ชุด)
 *
 * เขียนเป็น SVG เองเพราะโปรเจกต์ไม่มีไลบรารีกราฟ และหน้าภาพรวมต้องการแค่สองแบบนี้
 * กติกาตาม dataviz:
 *   · แท่งหนาไม่เกิน 24px ปลายมน 4px ฐานเหลี่ยม · เส้นหนา 2px · จุดปลายเส้นมีวงขาว 2px
 *   · เส้นตาราง 1px สีอ่อน · ตัวอักษรใช้สี ink ไม่ใช้สีของชุดข้อมูล
 *   · 2 ชุดขึ้นไปมีคำอธิบายสีเสมอ — ไม่สื่อความหมายด้วยสีอย่างเดียว
 *   · ชี้หรือกดลูกศรซ้าย/ขวาแล้วเห็นค่าทุกชุดของช่วงนั้น · ทุกค่าเปิดดูเป็นตารางได้
 *
 * ⚠️ ตัวเลขพิกัดใน SVG เป็นหน่วยของ viewBox ไม่ใช่ขนาดบนจอ — ใช้ token ไม่ได้
 */

export interface TrendSeries {
  key: string;
  label: string;
  /** สีของเส้น/แท่ง — ใช้ token สีกราฟเท่านั้น เช่น var(--chart-series-1) */
  color: string;
  values: number[];
}

export interface TrendChartProps {
  /** ชื่อกราฟ — ใช้เป็นชื่อของกลุ่มให้โปรแกรมอ่านหน้าจอ และหัวตาราง */
  title: string;
  /** ป้ายแกนนอนแบบสั้น (1 ต.ค. · ต.ค. 69) — ยาวเท่ากับ values ทุกชุด */
  labels: string[];
  /** ป้ายเต็มสำหรับ tooltip และตาราง — ไม่ส่งใช้ labels */
  fullLabels?: string[];
  series: TrendSeries[];
  kind: 'column' | 'line';
  /** จัดรูปแบบตัวเลข (ไม่ใส่หน่วย) — ใช้ใน tooltip และตาราง */
  format?: (n: number) => string;
  /** หน่วย — ต่อท้ายค่าใน tooltip และอยู่ในหัวคอลัมน์ของตาราง (ไม่ซ้ำในทุกช่อง) */
  unit?: string;
  /** ข้อมูลเป็นจำนวนนับ — เส้นกลางของแกนต้องเป็นเลขเต็ม ไม่ขึ้น 2.5 คน */
  integer?: boolean;
  className?: string;
}

const HEIGHT = 180;
const PAD = { top: 12, right: 12, bottom: 24, left: 48 };
const BAR_MAX = 24;
const BAR_GAP = 2;
const RADIUS = 4;

const defaultFormat = (n: number) => n.toLocaleString('th-TH', { maximumFractionDigits: 2 });

/** ย่อเลขบนแกน — 1,250 → 1.3K · 1,250,000 → 1.3M */
const compact = (n: number) => {
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toLocaleString('th-TH', { maximumFractionDigits: 1 })}M`;
  if (abs >= 1_000) return `${(n / 1_000).toLocaleString('th-TH', { maximumFractionDigits: 1 })}K`;
  return n.toLocaleString('th-TH', { maximumFractionDigits: 2 });
};

/** ค่าสูงสุดของแกนให้เป็นเลขกลม ๆ — 0 ทั้งชุดยังได้แกน 0–1 ไม่หาร 0 */
function niceMax(max: number): number {
  if (max <= 0) return 1;
  const pow = 10 ** Math.floor(Math.log10(max));
  const n = max / pow;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return step * pow;
}

/** แท่งปลายมนฐานเหลี่ยม — แท่งเตี้ยกว่ารัศมีใช้สี่เหลี่ยมธรรมดา */
function columnPath(x: number, y: number, w: number, h: number): string {
  if (h <= 0) return '';
  const r = Math.min(RADIUS, w / 2, h);
  return `M${x},${y + h}V${y + r}Q${x},${y} ${x + r},${y}H${x + w - r}Q${x + w},${y} ${x + w},${y + r}V${y + h}Z`;
}

export const TrendChart: React.FC<TrendChartProps> = ({
  title, labels, fullLabels, series, kind, format = defaultFormat, unit, integer = false, className,
}) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(600);
  const [active, setActive] = useState<number | null>(null);
  const [asTable, setAsTable] = useState(false);

  /** กว้างตามกล่องจริง — ไม่ยืด SVG แบบ preserveAspectRatio="none" เพราะตัวอักษรจะบิด */
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const measure = () => setWidth(Math.max(240, Math.round(el.getBoundingClientRect().width)));
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [asTable]);

  const n = labels.length;
  const full = fullLabels ?? labels;
  const plotW = width - PAD.left - PAD.right;
  const plotH = HEIGHT - PAD.top - PAD.bottom;
  const band = n > 0 ? plotW / n : plotW;
  const yMax = useMemo(() => {
    const m = niceMax(Math.max(0, ...series.flatMap((s) => s.values)));
    return integer && m % 2 !== 0 ? m + 1 : m;
  }, [series, integer]);
  const y = (v: number) => PAD.top + plotH - (Math.max(0, v) / yMax) * plotH;
  const xCenter = (i: number) => PAD.left + band * i + band / 2;

  /** ป้ายแกนนอนแบบเลือก — หัว กลาง ท้าย ไม่ให้ชนกัน */
  const xTicks = useMemo(() => {
    if (n === 0) return [];
    const set = new Set([0, Math.floor((n - 1) / 2), n - 1]);
    return [...set].sort((a, b) => a - b);
  }, [n]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (n === 0) return;
    if (e.key === 'ArrowRight') { e.preventDefault(); setActive((a) => (a === null ? n - 1 : Math.min(n - 1, a + 1))); }
    if (e.key === 'ArrowLeft') { e.preventDefault(); setActive((a) => (a === null ? n - 1 : Math.max(0, a - 1))); }
    if (e.key === 'Escape') setActive(null);
  };

  /** tooltip อยู่ฝั่งตรงข้ามกับครึ่งที่ชี้ — ไม่ล้นขอบกล่องไม่ว่าข้อความยาวแค่ไหน */
  const tooltipStyle: React.CSSProperties | undefined = active === null
    ? undefined
    : xCenter(active) > width / 2
      ? { right: width - xCenter(active) + 8, top: 0 }
      : { left: xCenter(active) + 8, top: 0 };

  return (
    <div className={cn('flex flex-col gap-[var(--space-2)]', className)}>
      <div className="flex flex-wrap items-center gap-[var(--space-3)]">
        {series.length > 1 && (
          <ul aria-label="คำอธิบายสี" className="m-0 flex list-none flex-wrap gap-[var(--space-3)] p-0">
            {series.map((s) => (
              <li key={s.key} className="flex items-center gap-[var(--space-1)] text-[length:var(--text-table-sm)] text-[var(--ink-700)]">
                {kind === 'line'
                  ? <span aria-hidden="true" className="inline-block h-[var(--space-05)] w-[var(--space-4)] rounded-[var(--radius-sm)]" style={{ background: s.color }} />
                  : <span aria-hidden="true" className="inline-block h-[var(--space-25)] w-[var(--space-25)] rounded-[var(--radius-sm)]" style={{ background: s.color }} />}
                {s.label}
              </li>
            ))}
          </ul>
        )}
        <button
          type="button"
          onClick={() => setAsTable((v) => !v)}
          aria-pressed={asTable}
          className="ml-auto inline-flex min-h-[var(--hit-icon-sm)] items-center gap-[var(--space-1)] rounded-[var(--radius-sm)] px-[var(--space-2)] text-[length:var(--text-table-sm)] font-[var(--weight-medium)] text-[var(--blue-700)] hover:bg-[var(--blue-050)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
        >
          <Icon name={asTable ? 'monitoring' : 'table'} size={16} />
          {asTable ? 'ดูเป็นกราฟ' : 'ดูเป็นตาราง'}
        </button>
      </div>

      {asTable ? (
        <div className="max-h-[calc(var(--space-20)*3)] overflow-auto">
          <table className="w-full text-[length:var(--text-table-sm)] leading-[var(--leading-table)]">
            <caption className="sr-only">{title}</caption>
            <thead>
              <tr className="text-left text-[var(--ink-600)]">
                <th scope="col" className="sticky top-0 bg-[var(--surface-0)] py-[var(--space-1)] pr-[var(--space-2)] font-[var(--weight-medium)]">ช่วง</th>
                {series.map((s) => (
                  <th key={s.key} scope="col" className="sticky top-0 bg-[var(--surface-0)] py-[var(--space-1)] pr-[var(--space-2)] text-right font-[var(--weight-medium)]">
                    {s.label}{unit ? ` (${unit})` : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {full.map((label, i) => (
                <tr key={`${i}-${label}`} className="border-t border-[var(--line-200)]">
                  <th scope="row" className="py-[var(--space-1)] pr-[var(--space-2)] text-left font-[var(--weight-regular)] text-[var(--ink-700)]">{label}</th>
                  {series.map((s) => (
                    <td key={s.key} className="bo-num py-[var(--space-1)] pr-[var(--space-2)] text-[var(--ink-900)]">{format(s.values[i] ?? 0)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div
          ref={wrapRef}
          role="group"
          aria-label={`${title} — ใช้ลูกศรซ้ายขวาเพื่ออ่านค่าแต่ละช่วง`}
          tabIndex={0}
          onKeyDown={onKeyDown}
          onBlur={() => setActive(null)}
          onMouseLeave={() => setActive(null)}
          className="relative rounded-[var(--radius-sm)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
        >
          <svg width={width} height={HEIGHT} viewBox={`0 0 ${width} ${HEIGHT}`} aria-hidden="true" style={{ display: 'block' }}>
            {/* เส้นตาราง 3 ระดับ + ป้ายแกนตั้ง */}
            {[0, 0.5, 1].map((t) => {
              const yy = PAD.top + plotH - t * plotH;
              return (
                <g key={t}>
                  <line x1={PAD.left} x2={width - PAD.right} y1={yy} y2={yy} stroke="var(--chart-grid)" strokeWidth={1} />
                  <text x={PAD.left - 6} y={yy} dy="0.32em" textAnchor="end" fill="var(--ink-600)" style={{ fontSize: 'var(--text-table-sm)' }}>
                    {compact(yMax * t)}
                  </text>
                </g>
              );
            })}

            {kind === 'column' && series[0] && series[0].values.map((v, i) => {
              const w = Math.max(2, Math.min(BAR_MAX, band - BAR_GAP));
              const top = y(v);
              return (
                <path
                  key={i}
                  d={columnPath(xCenter(i) - w / 2, top, w, PAD.top + plotH - top)}
                  fill={series[0]!.color}
                  opacity={active === null || active === i ? 1 : 0.45}
                />
              );
            })}

            {kind === 'line' && (
              <>
                {active !== null && (
                  <line x1={xCenter(active)} x2={xCenter(active)} y1={PAD.top} y2={PAD.top + plotH} stroke="var(--line-300)" strokeWidth={1} />
                )}
                {series.map((s) => {
                  const d = s.values.map((v, i) => `${i === 0 ? 'M' : 'L'}${xCenter(i)},${y(v)}`).join('');
                  const last = s.values.length - 1;
                  return (
                    <g key={s.key}>
                      <path d={d} fill="none" stroke={s.color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
                      {last >= 0 && (
                        <circle cx={xCenter(last)} cy={y(s.values[last] ?? 0)} r={4} fill={s.color} stroke="var(--surface-0)" strokeWidth={2} />
                      )}
                      {active !== null && (
                        <circle cx={xCenter(active)} cy={y(s.values[active] ?? 0)} r={4} fill={s.color} stroke="var(--surface-0)" strokeWidth={2} />
                      )}
                    </g>
                  );
                })}
              </>
            )}

            {/* ป้ายแกนนอนแบบเลือก */}
            {xTicks.map((i) => (
              <text
                key={i}
                x={xCenter(i)}
                y={HEIGHT - 6}
                textAnchor={i === 0 && n > 1 ? 'start' : i === n - 1 && n > 1 ? 'end' : 'middle'}
                fill="var(--ink-600)"
                style={{ fontSize: 'var(--text-table-sm)' }}
              >
                {labels[i]}
              </text>
            ))}

            {/* พื้นที่ชี้ — ทั้งความสูงของช่วง ใหญ่กว่าแท่งหรือเส้นเสมอ */}
            {labels.map((_, i) => (
              <rect
                key={i}
                x={PAD.left + band * i}
                y={PAD.top}
                width={band}
                height={plotH}
                fill="transparent"
                onMouseEnter={() => setActive(i)}
              />
            ))}
          </svg>

          {active !== null && (
            <div
              role="status"
              className="pointer-events-none absolute z-10 flex flex-col whitespace-nowrap gap-[var(--space-05)] rounded-[var(--radius-sm)] border border-[var(--line-200)] bg-[var(--surface-0)] px-[var(--space-2)] py-[var(--space-15)] shadow"
              style={tooltipStyle}
            >
              <span className="text-[length:var(--text-table-sm)] text-[var(--ink-600)]">{full[active]}</span>
              {series.map((s) => (
                <span key={s.key} className="flex items-center gap-[var(--space-1)] text-[length:var(--text-table-sm)] text-[var(--ink-700)]">
                  <span aria-hidden="true" className="inline-block h-[var(--space-05)] w-[var(--space-3)]" style={{ background: s.color }} />
                  <strong className="bo-num text-[var(--ink-900)]" style={{ textAlign: 'left' }}>
                    {format(s.values[active] ?? 0)}{unit ? ` ${unit}` : ''}
                  </strong>
                  {series.length > 1 && s.label}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
